import URL from 'url';

export enum ImagesSizes {
  default = '490x300',

  thumbnail = '200x200',

  // card/preview sizes with 3:2 aspect ratio
  xsmall = '360x240',
  small = '450x300',
  medium = '768x512',
  large = '1050x700',
  xlarge = '1860x1240',

  // fullscreen sizes
  fullscreenDesktopSmall = '1440x701',
  fullscreenDesktopMedium = '1920x881',
  fullscreenDesktopLarge = '2560x1241',
  fullscreenMobileSmall = '375x595', // portrait
  fullscreenMobileMedium = '414x664', // portrait
  fullscreenMobileLarge = '768x952', // portrait
}

const TRANSFORMATION_VALUES = ['crop', 'fit'];

const ROTATION_VALUES = ['0', '90', '180', '270'];

export interface ImageOptions {
  size?: ImagesSizes;
  transformation?: 'crop' | 'fit';
  rotation?: '0' | '90' | '180' | '270';
}

const OWNER_TYPE_MAP = {
  room: 'listing',
  hauser: 'user',
  uni_logo: 'unilogo',
  uni_photo: 'uniphoto',
};

/**
 * RegExp that matches Bosh URL (`https://$SOURCE/images/$CATEGORY/$OWNER_ID/$SIZE/$FILE_UUID?t=$TRANSFORMATION&=$ROTATION`)
 */
const BOSCH_URL_RE =
  /https:\/\/thumb\.(?:.*\.)?housinganywhere\.com\/images\/(?:listing|user|unilogo|uniphoto)\/\d+\/(?:default|raw|\d+x\d+)\/[0-9a-f-]+/i;

/**
 * RegExp that matches supported legacy URLs
 * - housinganywhere.com/images/room/
 * - housinganywhere.com/v2/images/room/
 * - housinganywhere.com/images/uni_logo/
 */
const LEGACY_URL_RE =
  /housinganywhere\.com(?:\/v2)?\/images\/(?:room|hauser|uni_logo|uni_photo)\//i;

/**
 * Generates transformed image URL using Bosch
 * check https://github.com/housinganywhere/src/blob/master/docs/image-processing-apis.md for specifications.
 * check https://housinganywhere.atlassian.net/wiki/spaces/PROD/pages/1274150954/Image+specs "BE dimensions" section.
 * @param url image URL from platform
 */
// eslint-disable-next-line complexity
function transformImageUrl(url: string, options: ImageOptions = {}) {
  if (!LEGACY_URL_RE.test(url) && !BOSCH_URL_RE.test(url)) {
    return url;
  }

  const parsedUrl = URL.parse(url);

  let transformation = options?.transformation;
  let rotation = options?.rotation;
  const size = options?.size ?? ImagesSizes.default;

  let urlOwnerType;
  let urlOwnerId;
  let urlImageSize;
  let urlImageId;

  if (BOSCH_URL_RE.test(url)) {
    // use bosch parameters (t, r, ...) as fallback when an option is not provided.
    if (
      typeof URLSearchParams !== 'undefined' &&
      (!transformation || !rotation) &&
      parsedUrl.search
    ) {
      const urlSearchParams =
        // @ts-ignore
        parsedUrl.searchParams || new URLSearchParams(parsedUrl.search);

      if (!rotation && urlSearchParams.has('r')) {
        const value = urlSearchParams.get('r');

        rotation =
          value && value !== '0' && ROTATION_VALUES.includes(value)
            ? (value as ImageOptions['rotation'])
            : undefined;
      }

      if (!transformation && urlSearchParams.has('t')) {
        const value = urlSearchParams.get('t');

        transformation =
          value && value !== '0' && TRANSFORMATION_VALUES.includes(value)
            ? (value as ImageOptions['transformation'])
            : undefined;
      }
    }

    // extract key information from pathname
    [, , urlOwnerType, urlOwnerId, urlImageSize, urlImageId] = (
      parsedUrl.pathname || ''
    ).split('/');

    if (urlImageSize !== 'default') {
      console.warn(`${url} has already been processed with transformImageUrl`);
    }
  } else {
    // use legacy parameter (rotate, nocrop, ...) as fallback when an option is not provided
    if (
      typeof URLSearchParams !== 'undefined' &&
      (!transformation || !rotation) &&
      parsedUrl.query
    ) {
      const urlSearchParams = new URLSearchParams(parsedUrl.query);

      if (!rotation && urlSearchParams.has('rotate')) {
        const value = urlSearchParams.get('rotate');

        rotation =
          value && value !== '0' && ROTATION_VALUES.includes(value)
            ? (value as ImageOptions['rotation'])
            : undefined;
      }

      if (!transformation && urlSearchParams.has('nocrop')) {
        const value = urlSearchParams.get('nocrop');

        transformation =
          value && value !== '0' && value !== 'false' ? 'fit' : undefined;
      }
    }

    // extract key information from pathname
    const cleanUrl = (parsedUrl.pathname || '')
      .replace('/v2/', '/')
      .replace(/\.(png|jpg|webp)/g, '');

    [, , urlOwnerType, urlOwnerId, urlImageId] = cleanUrl.split('/');

    urlOwnerType = OWNER_TYPE_MAP[urlOwnerType] || urlOwnerType;
  }

  parsedUrl.pathname = `/images/${urlOwnerType}/${urlOwnerId}/${size}/${urlImageId}`;

  if (typeof URLSearchParams !== 'undefined' && (transformation || rotation)) {
    const urlSearchParams = new URLSearchParams('');

    if (transformation) {
      urlSearchParams.set('t', transformation);
    }

    if (rotation) {
      urlSearchParams.set('r', rotation);
    }

    parsedUrl.search = urlSearchParams.toString();
  } else {
    parsedUrl.search = '';
  }

  return URL.format(parsedUrl);
}

export default transformImageUrl;
